import { useCallback, useRef } from 'react';

import {
  EVENT_ACTION_MEASURE,
  EVENT_CATEGORY_DURATION,
} from 'approot/shared/data-tracker/data-events.constants';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import { DataTrackerEventData } from './data-tracker.constants';
import customNanoId from 'lib/nanoid/nanoid';

/**
 * Captures the duration of an API or UI event across components and renders.
 * Tracks action "measure" with category "duration".
 *
 * The hook generates a unique token for the lifetime of the hook instance.
 * using the provided `targetType` and a custom nanoid. The caller must pass this
 * token to both `startMeasurement()` and `stopMeasurement()` to complete the measurement.
 *
 * @param targetType - Type of the interaction (ie. "tour-step", "search-result").
 * @param targetIdentifier - Optional identifier for the target if known at start time.
 *
 * @returns
 * - targetEventToken: Stable unique token for the hook instance.
 * - startMeasurement(targetEventToken): Starts timing for the provided token.
 * - stopMeasurement(targetEventToken, targetIdOverride?, targetEventSuccess?):
 *   Stops timing for the token and emits the measurement event.
 *   - `targetIdOverride` Required if no `targetIdentifier` was provided at start time.
 *   - `targetEventSuccess` Optional boolean for API response reporting.
 */

type UseCaptureEventDurationArgs = {
  targetType: string;
  targetIdentifier?: string | number;
};

const durationStore = new Map<string, number>();
const makeToken = (targetType: string) => `${targetType}:${customNanoId()}`;

export const useCaptureEventDuration = ({
  targetType,
  targetIdentifier,
}: UseCaptureEventDurationArgs): {
  targetEventToken: string;
  startMeasurement: (args: { targetEventToken: string }) => string;
  stopMeasurement: (args: {
    targetEventToken: string;
    targetIdOverride?: string | number;
    targetEventSuccess?: boolean;
  }) => void;
} => {
  const tokenRef = useRef<string>(makeToken(targetType));
  const startMeasurement = useCallback(
    ({ targetEventToken }: { targetEventToken: string }) => {
      durationStore.set(targetEventToken, performance.now());
      return targetEventToken;
    },
    []
  );

  const stopMeasurement = useCallback(
    ({
      targetEventToken,
      targetIdOverride,
      targetEventSuccess,
    }: {
      targetEventToken: string;
      targetIdOverride?: string | number;
      targetEventSuccess?: boolean;
    }) => {
      const startTime = durationStore.get(targetEventToken);
      if (startTime == null) return;

      const finalIdentifier = targetIdOverride ?? targetIdentifier;
      if (finalIdentifier == null) {
        durationStore.delete(targetEventToken);
        return;
      }

      const duration = performance.now() - startTime;
      const eventPayload: DataTrackerEventData = {
        target_type: targetType,
        target_identifier: finalIdentifier,
        duration,
      };

      if (targetEventSuccess !== undefined) {
        eventPayload.event_success = targetEventSuccess;
      }

      pushDataTrackerEvent(
        EVENT_CATEGORY_DURATION,
        EVENT_ACTION_MEASURE,
        eventPayload
      );
      durationStore.delete(targetEventToken);
    },
    [targetType, targetIdentifier]
  );

  return {
    targetEventToken: tokenRef.current,
    startMeasurement,
    stopMeasurement,
  };
};
